interface GetPathProps {
  fromX: number
  fromY: number
  toX: number
  toY: number
  direction: 'left' | 'right'
}

interface GetPathReturn {
  path: string
  midX: number
  midY: number
}

type GetPathFunction = (props: GetPathProps) => GetPathReturn

export const getVerticalBezierPath: GetPathFunction = ({ fromX, fromY, toX, toY, direction }) => {
  const extendedX = Math.abs(fromY - toY) * 0.5
  const flipped = direction === 'right' ? extendedX : -extendedX
  const weight = 0
  const { x: midX, y: midY } = getBezierMiddlePoint(fromX, fromY, fromX + flipped, toX + flipped, toY)
  return {
    path: `M${fromX},${fromY} C${fromX + flipped},${fromY - weight} ${toX + flipped},${toY + weight} ${toX},${toY}`,
    midX,
    midY,
  }
}

function getBezierMiddlePoint(
  sourceX: number,
  sourceY: number,
  controlPointX: number,
  targetX: number,
  targetY: number,
) {
  const t = 0.5
  const x =
    Math.pow(1 - t, 3) * sourceX +
    3 * Math.pow(1 - t, 2) * t * controlPointX +
    3 * (1 - t) * t * t * controlPointX +
    Math.pow(t, 3) * targetX
  const y =
    Math.pow(1 - t, 3) * sourceY +
    3 * Math.pow(1 - t, 2) * t * sourceY +
    3 * (1 - t) * t * t * targetY +
    Math.pow(t, 3) * targetY

  return { x, y }
}

export const getVerticalSmoothStepPath: GetPathFunction = ({ fromX, fromY, toX, toY, direction }) => {
  const curveNum = 20
  const distance = Math.abs(fromY - toY) * 0.5
  const extendedX = direction === 'right' ? fromX + distance : fromX - distance
  const midX =
    direction === 'right'
      ? extendedX < toX
        ? toX + curveNum
        : extendedX
      : extendedX > toX
      ? toX - curveNum
      : extendedX
  const x2 = direction === 'right' ? midX - curveNum : midX + curveNum
  const y2 = direction === 'right' ? fromY - curveNum : fromY + curveNum
  const y3 = direction === 'right' ? toY + curveNum : toY - curveNum
  return {
    path: `M${fromX},${fromY} H${x2} Q${midX},${fromY} ${midX},${y2} V${y3} Q${midX},${toY} ${x2},${toY} H${toX}`,
    midX,
    midY: Math.round((fromY + toY) / 2),
  }
}

export const getSelfConnectPath: GetPathFunction = ({ fromX, fromY, toX: _tx, toY, direction: _d }) => {
  const extendedX = 80
  const radius = 10
  const startX = fromX + 40
  const midY = (fromY + toY) / 2
  const cx = startX + extendedX
  const cy = midY
  return {
    path: `M${startX},${midY} A ${radius} ${radius} 0 0 0 ${cx} ${cy} A ${radius} ${radius} 0 1 0 ${startX},${midY}`,
    midX: startX + extendedX,
    midY,
  }
}
